import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Navigate, Link } from 'react-router-dom';
import { formatDate, formatDateTime } from '../utils/datetime';
import { Download, BarChart3, FileText, DoorOpen } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import apiClient from '../utils/apiClient';
import {
  buildCsv,
  buildAndDownloadPdf,
  downloadBlob,
} from '../utils/reportExports';

// Reports is the KPIs / catalog surface (2026-06-05). Invoices and
// Payments lists are top-level pages again (/invoices, /payments) —
// the earlier sub-tab consolidation got rolled back so daily ledgers
// don't hide behind a report selector.
//
// Old bookmarks such as /reports?view=invoices|payments redirect to the
// matching top-level page so nobody lands on a dead tab.

// ESS-style Reports: category sidebar -> a report -> filter + table +
// CSV/PDF export. Phase 1 (daily drivers) hits the real /reports/* API.

// Reports whose endpoint accepts ?start=&end=
const WINDOWED = new Set(['revenue', 'move-in-out']);

// Generic report cells: numbers get separators; ISO timestamps (naive UTC
// from the backend) render in the facility timezone; date-only strings render
// as calendar dates with no zone shift.
//
// Module scope deliberately. This lived inside <Reports> while being CALLED
// inside <KpisPanel> — a different component — so every rendered report row
// threw "formatCell is not defined" and the error boundary blanked the page.
// A build cannot catch that; only running it can.
const formatCell = (v, timeZone) => {
  if (typeof v === 'number') return v.toLocaleString();
  const str = String(v ?? '');
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str)) {
    return formatDateTime(str, timeZone);
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return formatDate(str);
  return str;
};

const KpisPanel = ({ facilityId, facilityName, facilityTimezone }) => {
  const [catalog, setCatalog] = useState([]);
  const [active, setActive] = useState(null); // {key,name,windowed}
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [range, setRange] = useState({ start: '', end: '' });

  const loadCatalog = useCallback(async () => {
    if (!facilityId) return;
    try {
      const r = await apiClient.get(
        `/api/facilities/${facilityId}/reports/_catalog`,
      );
      setCatalog(r.data.groups || []);
    } catch {
      setError('Could not load report catalog');
    }
  }, [facilityId]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const runReport = useCallback(
    async (rep) => {
      setActive(rep);
      setData(null);
      setError('');
      setLoading(true);
      try {
        let url = `/api/facilities/${facilityId}/reports/${rep.key}`;
        if (WINDOWED.has(rep.key) && (range.start || range.end)) {
          const qs = new URLSearchParams();
          if (range.start) qs.set('start', range.start);
          if (range.end) qs.set('end', range.end);
          url += `?${qs.toString()}`;
        }
        const r = await apiClient.get(url);
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Report failed');
      } finally {
        setLoading(false);
      }
    },
    [facilityId, range],
  );

  // Pick the first array-of-objects field as the table body.
  const tableRows =
    data &&
    (data.rows ||
      data.move_ins ||
      data.by_month ||
      (Array.isArray(data.by_type) ? data.by_type : null) ||
      []);

  const hasRows = tableRows && tableRows.length > 0;

  const onExportCsv = () => {
    if (!active || !hasRows) return;
    const csv = buildCsv(tableRows, {
      facilityName,
      reportName: active.name,
    });
    downloadBlob(`${active.key}.csv`, csv, 'text/csv');
  };

  const onExportPdf = () => {
    if (!active || !hasRows) return;
    buildAndDownloadPdf(tableRows, {
      facilityName,
      reportName: active.name,
      filename: `${active.key}.pdf`,
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-5">
      {/* Category sidebar */}
      <div className="space-y-4">
        {catalog.map((g) => (
          <div key={g.label}>
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
              {g.label}
            </p>
            <div className="space-y-0.5">
              {g.reports.map((rep) => (
                <button
                  key={rep.key}
                  onClick={() => runReport(rep)}
                  className={`w-full text-left px-3 py-1.5 rounded-md text-[13px] transition-colors ${
                    active?.key === rep.key
                      ? 'bg-emerald-600 text-white font-medium'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {rep.name}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Report panel */}
      <div className="rounded-xl border border-slate-200/80 bg-white p-5 min-h-[300px]">
        {!active ? (
          <div className="h-full flex flex-col items-center justify-center text-center py-16">
            <BarChart3 className="h-8 w-8 text-slate-300 mb-3" />
            <p className="text-[13px] text-slate-400">
              Select a report from the left.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="text-[15px] font-semibold text-slate-900">
                {active.name}
              </h2>
              <div className="flex items-center gap-2">
                {WINDOWED.has(active.key) && (
                  <>
                    <input
                      type="date"
                      value={range.start}
                      onChange={(e) =>
                        setRange({ ...range, start: e.target.value })
                      }
                      className="px-2 py-1.5 rounded-lg border border-slate-200 text-[12px]"
                    />
                    <span className="text-slate-400 text-[12px]">to</span>
                    <input
                      type="date"
                      value={range.end}
                      onChange={(e) =>
                        setRange({ ...range, end: e.target.value })
                      }
                      className="px-2 py-1.5 rounded-lg border border-slate-200 text-[12px]"
                    />
                    <button
                      onClick={() => runReport(active)}
                      className="px-3 py-1.5 rounded-lg bg-slate-100 text-[12px] font-medium text-slate-600 hover:bg-slate-200"
                    >
                      Apply
                    </button>
                  </>
                )}
                {hasRows && (
                  <>
                    <button
                      onClick={onExportPdf}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-[12px] font-medium"
                      title="Download as PDF"
                    >
                      <FileText className="h-3.5 w-3.5" /> PDF
                    </button>
                    <button
                      onClick={onExportCsv}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-[12px] font-medium"
                      title="Download as CSV"
                    >
                      <Download className="h-3.5 w-3.5" /> CSV
                    </button>
                  </>
                )}
              </div>
            </div>

            {error && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200/80 px-3 py-2.5 text-[13px] text-red-600">
                {error}
              </div>
            )}

            {/* Summary chips */}
            {data?.summary && (
              <div className="flex flex-wrap gap-3 mb-4">
                {Object.entries(data.summary).map(([k, v]) => (
                  <div
                    key={k}
                    className="rounded-lg bg-slate-50 border border-slate-200/60 px-3 py-2"
                  >
                    <p className="text-[10px] uppercase tracking-wider text-slate-400">
                      {k.replace(/_/g, ' ')}
                    </p>
                    <p className="text-[15px] font-semibold text-slate-900">
                      {typeof v === 'number'
                        ? v.toLocaleString()
                        : String(v)}
                    </p>
                  </div>
                ))}
              </div>
            )}
            {data?.buckets && (
              <div className="flex flex-wrap gap-3 mb-4">
                {Object.entries(data.buckets).map(([k, v]) => (
                  <div
                    key={k}
                    className="rounded-lg bg-amber-50 border border-amber-200/60 px-3 py-2"
                  >
                    <p className="text-[10px] uppercase tracking-wider text-amber-500">
                      {k} days
                    </p>
                    <p className="text-[15px] font-semibold text-amber-700">
                      ${Number(v).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {loading ? (
              <p className="text-[13px] text-slate-400 py-8 text-center">
                Loading…
              </p>
            ) : hasRows ? (
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 text-left">
                      {Object.keys(tableRows[0]).map((c) => (
                        <th
                          key={c}
                          className="px-3 py-2 font-medium whitespace-nowrap"
                        >
                          {c.replace(/_/g, ' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row, i) => (
                      <tr
                        key={i}
                        className="border-t border-slate-100 text-slate-700"
                      >
                        {Object.keys(tableRows[0]).map((c) => (
                          <td
                            key={c}
                            className="px-3 py-2 whitespace-nowrap"
                          >
                            {formatCell(row[c], facilityTimezone)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-[13px] text-slate-400 py-8 text-center">
                No data for this report.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const Reports = () => {
  const { facilityId, currentFacility, loading: facilityLoading } = useFacility();
  const [searchParams] = useSearchParams();

  // Back-compat for the prior /reports?view=invoices|payments URLs.
  // Anyone with an old bookmark gets bounced to the new top-level page
  // instead of staring at a tab that no longer exists.
  const legacyView = searchParams.get('view');
  if (legacyView === 'invoices') {
    return <Navigate to="/invoices" replace />;
  }
  if (legacyView === 'payments') {
    return <Navigate to="/payments" replace />;
  }

  if (facilityLoading) {
    return <div className="text-[13px] text-slate-400">Loading…</div>;
  }
  if (!facilityId) {
    return (
      <div className="text-[13px] text-slate-400">
        Select a facility to view reports.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-slate-400" />
            Reports
          </h1>
          <p className="text-[13px] text-slate-400 mt-0.5">
            Operational and financial reports for this facility.
          </p>
        </div>
        <Link
          to="/reports/gate-activity"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-[12px] font-medium"
        >
          <DoorOpen className="h-3.5 w-3.5" /> Gate Activity Log
        </Link>
      </div>

      <KpisPanel
        facilityId={facilityId}
        facilityName={currentFacility?.name || ''}
        facilityTimezone={currentFacility?.timezone}
      />
    </div>
  );
};

export default Reports;
